import React, { useState, useEffect, useCallback, useRef } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { Toaster, toast } from "react-hot-toast";
import Sidebar from "./components/Sidebar";
import TopBar from "./components/TopBar";
import Homepage from "./components/Homepage";
import ConfigurationsPage from "./components/ConfigurationsPage";
import OIDCLogin from "./components/OIDCLogin";
import FileManagement from "./components/FileManagement";
import ProductsManagement from "./components/ProductsManagement";
import CacheManagement from "./components/CacheManagement";
import VectorStoreStats from "./components/VectorStoreStats";
import SimilaritySearch from "./components/SimilaritySearch";
import TenantManagement from "./components/TenantManagement";
import StoreLocationsManagement from "./components/StoreLocationsManagement";
import WidgetSettingsPage from "./components/WidgetSettingsPage";
import ConversationsWorkspace from "./components/ConversationsWorkspace";
import AnalyticsPage from "./components/AnalyticsPage";
import SuperAdminRoute from "./components/SuperAdminRoute";
import AdminRoute from "./components/AdminRoute";
import RoleRoute from "./components/RoleRoute";
import NotFound from "./components/NotFound";
import Breadcrumbs from "./components/Breadcrumbs";
import TenantEntryBanner from "./components/TenantEntryBanner";
import { configApi } from "./services/api";
import { tenantApi } from "./services/tenantApi";
import { useLanguage } from "./contexts/LanguageContext";
import { useAuth } from "./contexts/AuthContext";
import { useChat } from "./contexts/ChatContext";
import { UploadProvider } from "./contexts/UploadContext";
import { ChatProvider } from "./contexts/ChatContext";
import { editableConfigTypesFor } from "./utils/permissions";
import { MANAGER, CONTENT_SECTION_ROLES, CONVERSATIONS, ANALYTICS, holdsDepartmentRole } from "./utils/roles";

const LoadingScreen = ({ message }) => (
  <div
    className="min-h-screen flex items-center justify-center"
    style={{ backgroundColor: "var(--color-bg)" }}
  >
    <div className="text-center">
      <div
        className="w-10 h-10 rounded-full border-2 border-t-transparent mx-auto mb-4"
        style={{
          borderColor: "var(--color-mint)",
          borderTopColor: "transparent",
          animation: "spin 0.8s linear infinite",
        }}
      />
      <p className="text-sm font-medium" style={{ color: "var(--color-text-secondary)" }}>
        {message || "Loading..."}
      </p>
    </div>
  </div>
);

const AppContent = () => {
  // A pagina de conversas e uma folha que preenche o que sobra do <main>,
  // com scroll dentro de cada coluna. Para isso o <main> tem de ser uma
  // coluna flex nessa rota; nas outras rotas o conteudo empilha e rola como
  // sempre, e nada muda.
  const location = useLocation();
  const fillsMain = location.pathname.startsWith("/conversations");

  const { translate } = useLanguage();
  const {
    isAuthenticated,
    isLoading: authLoading,
    isEffectivelyAdmin,
    isPlatformOnlySuperAdmin,
    isSuperAdmin,
    enteredTenant,
    user,
    effectiveRoles,
  } = useAuth();
  const { selectedChatId } = useChat();

  const [configs, setConfigs] = useState({});
  // What the backoffice should hide, reported by the API alongside `configs` and applied
  // at render time. Deliberately NOT part of `configs`: that object is echoed back to the
  // API on save, so anything synthetic in it gets persisted and anything missing from it
  // gets deleted.
  const [visibility, setVisibility] = useState({});
  // The config types this chat has NO stored row for, reported by the API alongside
  // `configs` (RPAA-225 / backend #235). Outside `configs` for the same reason
  // `visibility` is: that object is echoed back on save, and a type that is missing is
  // not content to persist. The page builds a tab for each of these so the operator has
  // somewhere to create the row from, which is the only sanctioned way to prepare a new
  // chat now that the backend stopped answering with the code defaults.
  const [missingTypes, setMissingTypes] = useState([]);
  const [filteredConfigs, setFilteredConfigs] = useState({});
  const [filteredMissingTypes, setFilteredMissingTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [companyName, setCompanyName] = useState(null);
  const [isTenantNameReady, setIsTenantNameReady] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Generation counter mirroring ChatContext's own tenant-switch guard: a
  // stale in-flight fetchConfigs() from the previous tenant must not
  // clobber state after a newer tenant change (RPAA-213 lower-severity
  // finding — configs were the one piece of tenant-scoped state that
  // didn't get this treatment).
  const configGeneration = useRef(0);

  // Clear configs on every tenant change so a Save can't land mid-switch
  // against the previous tenant's stale values still sitting in state.
  useEffect(() => {
    configGeneration.current += 1;
    setConfigs({});
    setFilteredConfigs({});
    // Also the visibility report: it describes the tenant that produced it, so leaving
    // it behind would group and hide the next tenant's fields by the previous one's
    // platform until the fetch lands.
    setVisibility({});
    setMissingTypes([]);
    setFilteredMissingTypes([]);
  }, [enteredTenant?.id]);

  // Update fetchConfigs to work with OIDC roles and chat_id
  const fetchConfigs = useCallback(async () => {
    if (isPlatformOnlySuperAdmin()) {
      setIsLoading(false);
      return;
    }

    // Bump on every call (not just the tenant-reset effect) so two
    // overlapping fetchConfigs() calls for the same tenant — rapid
    // selectedChatId changes, StrictMode's dev double-invoke — can't apply
    // out of order either, matching ChatContext's loadChats() parity.
    configGeneration.current += 1;
    const myGeneration = configGeneration.current;
    setIsLoading(true);
    setError(null);
    try {
      let data = {};
      let nextVisibility = {};
      let nextMissingTypes = [];
      if (isEffectivelyAdmin()) {
        const response = await configApi.getAllConfigs(selectedChatId);
        if (response.success) {
          data = response.data;
          nextVisibility = response.visibility || {};
          /* Only this branch reports it: the single-type route still answers 404 for a
             chat with no row, so the non-admin path below has no list to read. A manager
             who lands on a row-less chat sees the same empty page as before. */
          nextMissingTypes = response.missing_types || [];
        } else {
          throw new Error(translate("failedToLoadConfigurations"));
        }
      } else {
        const response = await configApi.getConfig("user_editable", selectedChatId);
        let userConfig = response.data;
        if (userConfig && userConfig.data) {
          userConfig = userConfig.data;
        }
        data = { user_editable: userConfig };
      }
      if (myGeneration !== configGeneration.current) return;
      setConfigs(data);
      // Behind the same guard as setConfigs: an in-flight response from the previous
      // tenant must not paint this one's toggles.
      setVisibility(nextVisibility);
      setMissingTypes(nextMissingTypes);
    } catch (err) {
      if (myGeneration !== configGeneration.current) return;
      console.error("Error fetching configs:", err);
      setError(err.message);
      toast.error(translate("failedToLoadConfigurations"));
    } finally {
      if (myGeneration === configGeneration.current) {
        setIsLoading(false);
      }
    }
  }, [isEffectivelyAdmin, isPlatformOnlySuperAdmin, translate, selectedChatId]);

  // Filter configs to the types the user's roles may edit (additive)
  useEffect(() => {
    if (isAuthenticated && configs) {
      const allowedConfigs = editableConfigTypesFor(effectiveRoles());
      const filtered = {};
      Object.entries(configs).forEach(([configType, config]) => {
        if (allowedConfigs.includes(configType)) {
          filtered[configType] = config;
        }
      });
      setFilteredConfigs(filtered);
      /* The same filter, and in the same place, because a missing type's tab exists to
         CREATE the row: offering one for a type the user may not PUT would be a tab
         whose only action 403s. */
      setFilteredMissingTypes(missingTypes.filter((configType) => allowedConfigs.includes(configType)));
    }
  }, [isAuthenticated, configs, missingTypes, user, effectiveRoles]);

  useEffect(() => {
    if (isAuthenticated && !isPlatformOnlySuperAdmin()) {
      fetchConfigs();
    } else if (isAuthenticated && isPlatformOnlySuperAdmin()) {
      setIsLoading(false);
    }
  }, [isAuthenticated, isPlatformOnlySuperAdmin, fetchConfigs]);

  useEffect(() => {
    if (isAuthenticated && !isPlatformOnlySuperAdmin() && selectedChatId) {
      fetchConfigs();
    }
  }, [selectedChatId, isAuthenticated, isPlatformOnlySuperAdmin, fetchConfigs]);

  useEffect(() => {
    const fetchTenantName = async () => {
      if (isPlatformOnlySuperAdmin()) {
        setCompanyName(null);
        setIsTenantNameReady(true);
        return;
      }
      // Only trust enteredTenant as a shortcut while the user is still a
      // super admin — a stale/leftover enteredTenant value (e.g. from before
      // a role revocation) must not be used as the company name for a
      // non-super-admin user (RPAA-213 finding #5).
      if (enteredTenant && isSuperAdmin()) {
        setCompanyName(enteredTenant.name);
        setIsTenantNameReady(true);
        return;
      }
      try {
        const response = await tenantApi.getMyTenantName();
        setCompanyName(response?.name || "");
        setIsTenantNameReady(true);
      } catch (error) {
        console.error("Error fetching tenant name:", error);
        setCompanyName("");
        setIsTenantNameReady(true);
      }
    };
    if (isAuthenticated) {
      fetchTenantName();
    }
  }, [isAuthenticated, isPlatformOnlySuperAdmin, isSuperAdmin, enteredTenant]);

  const handleUpdateConfig = async (configType, data) => {
    if (!editableConfigTypesFor(effectiveRoles()).includes(configType)) {
      toast.error(translate("noPermissionToModify"));
      return;
    }
    try {
      const response = await configApi.updateConfig(configType, data, selectedChatId);
      if (response.success) {
        await fetchConfigs();
        toast.success(translate("configurationUpdated", { configType }));
      } else {
        throw new Error(response.message || translate("failedToUpdateConfiguration"));
      }
    } catch (err) {
      console.error("Error updating config:", err);
      toast.error(translate("failedToUpdate", { configType, message: err.message }));
      throw err;
    }
  };

  if (authLoading) {
    return <LoadingScreen message={translate("loading") || "Loading..."} />;
  }

  if (!isAuthenticated) {
    return <OIDCLogin />;
  }

  if (!isTenantNameReady) {
    return <LoadingScreen message={translate("loading") || "Loading..."} />;
  }

  return (
    <div className="flex h-screen overflow-hidden" style={{ backgroundColor: "var(--color-bg)" }}>
      <Toaster
        position="bottom-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: "var(--color-surface)",
            color: "var(--color-text-primary)",
            border: "1px solid var(--color-border)",
            borderRadius: "10px",
            fontFamily: "'Inter', sans-serif",
            fontSize: "14px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          },
          success: {
            duration: 3000,
            iconTheme: { primary: "#3ecf8e", secondary: "#ffffff" },
          },
          error: {
            duration: 5000,
            iconTheme: { primary: "#ef4444", secondary: "#ffffff" },
          },
        }}
      />

      {/* Sidebar */}
      <Sidebar
        companyName={companyName}
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
      />

      {/* Main content area */}
      <div
        className="flex flex-col flex-1 overflow-hidden lg:ml-[256px]"
      >
        <TopBar onMenuClick={() => setIsSidebarOpen(true)} />

        <TenantEntryBanner />

        <main className={`flex-1 overflow-y-auto p-6 ${fillsMain ? "flex flex-col" : ""}`}>
          <Breadcrumbs />

          {error && !isPlatformOnlySuperAdmin() && (
            <div
              className="mb-6 p-3 rounded-xl border text-sm"
              style={{
                backgroundColor: "#fef2f2",
                borderColor: "#fecaca",
                color: "#dc2626",
              }}
            >
              {translate("error")}: {error}
            </div>
          )}

          <Routes>
            <Route path="/" element={<Homepage />} />
            <Route
              path="/configs"
              element={
                <RoleRoute anyOf={[MANAGER]}>
                  <ConfigurationsPage
                    configs={filteredConfigs}
                    missingTypes={filteredMissingTypes}
                    visibility={visibility}
                    isLoading={isLoading}
                    onUpdate={handleUpdateConfig}
                  />
                </RoleRoute>
              }
            />
            <Route
              path="/widget"
              element={
                <RoleRoute anyOf={[MANAGER]}>
                  <WidgetSettingsPage />
                </RoleRoute>
              }
            />
            <Route
              path="/files/*"
              element={
                <RoleRoute anyOf={CONTENT_SECTION_ROLES} allow={holdsDepartmentRole}>
                  <FileManagement />
                </RoleRoute>
              }
            />
            <Route path="/products" element={<AdminRoute><ProductsManagement /></AdminRoute>} />
            <Route path="/cache" element={<AdminRoute><CacheManagement /></AdminRoute>} />
            <Route path="/vector-store" element={<AdminRoute><VectorStoreStats /></AdminRoute>} />
            <Route path="/similarity-search" element={<AdminRoute><SimilaritySearch /></AdminRoute>} />
            <Route
              path="/tenants"
              element={
                <SuperAdminRoute>
                  <TenantManagement />
                </SuperAdminRoute>
              }
            />
            <Route path="/store-locations" element={<RoleRoute anyOf={[MANAGER]}><StoreLocationsManagement /></RoleRoute>} />
            <Route path="/store-locations/list" element={<RoleRoute anyOf={[MANAGER]}><StoreLocationsManagement /></RoleRoute>} />
            <Route path="/store-locations/map" element={<RoleRoute anyOf={[MANAGER]}><StoreLocationsManagement /></RoleRoute>} />
            <Route path="/store-locations/warehouses" element={<RoleRoute anyOf={[MANAGER]}><StoreLocationsManagement /></RoleRoute>} />
            {/* As duas rotas montam a mesma concha de dois paineis: a lista a
                esquerda em ambas, a transcricao a direita quando ha :id. O
                deep link, o destaque da barra lateral e os guardas ficam
                exatamente como estavam. */}
            <Route
              path="/conversations"
              element={
                <RoleRoute anyOf={[CONVERSATIONS, MANAGER]}>
                  <ConversationsWorkspace />
                </RoleRoute>
              }
            />
            <Route
              path="/conversations/:id"
              element={
                <RoleRoute anyOf={[CONVERSATIONS, MANAGER]}>
                  <ConversationsWorkspace />
                </RoleRoute>
              }
            />
            {/* RPAA-286: a pagina de analytics. O mesmo `anyOf` da barra lateral e do atalho da
                pagina inicial; o admin passa sempre pelo canSeeSection. */}
            <Route
              path="/analytics"
              element={
                <RoleRoute anyOf={[ANALYTICS, MANAGER]}>
                  <AnalyticsPage />
                </RoleRoute>
              }
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

function App() {
  return (
    <ChatProvider>
      <UploadProvider>
        <AppContent />
      </UploadProvider>
    </ChatProvider>
  );
}

export default App;
